/**
 * WorldMap Component
 *
 * Dashboard world map showing locations with color-coded test execution status.
 * Status colors: green (success), yellow (warning), red (error), gray (inactive), blue (executing)
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, InfoWindow, Marker } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { MapPin, Smartphone, Activity, CheckCircle2, AlertTriangle, XCircle, Clock, Globe, Plus } from 'lucide-react';
import { Card, Spinner, Badge, Button } from '@/components/ui';
import { useTheme } from '@/providers/ThemeProvider';
import {
  useLocationExecutionStatus,
  LocationExecutionStats,
  LocationStatus,
  STATUS_COLORS,
  getStatusLabel,
} from '@/hooks/useLocationExecutionStatus';
import { getMapOptions, MINIMALIST_MAP_STYLES } from '@/config/mapStyles';
import { AdvancedMarker } from '@/components/maps/AdvancedMarker';
import { useDateFormat } from '@/hooks/useDateFormat';

// 'marker' library is required for AdvancedMarkerElement; loaded unconditionally
// so the component can mount as soon as a mapId is present in env.
const GOOGLE_MAPS_LIBRARIES: ('places' | 'marker')[] = ['places', 'marker'];

interface WorldMapProps {
  height?: string;
  onLocationClick?: (locationId: string) => void;
  refreshInterval?: number; // Auto-refresh interval in ms (default 30000)
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = { lat: 30.0, lng: 0.0 };

const STATUS_ICONS: Record<LocationStatus, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  inactive: Clock,
  executing: Activity,
};

/**
 * Generate SVG marker with status color
 */
function generateMarkerSvg(status: LocationStatus, isSelected: boolean): string {
  const color = STATUS_COLORS[status];
  const size = isSelected ? 20 : 14;
  const pulse = status === 'executing' ? 'animate' : '';

  // For executing status, we'll add a pulsing effect via CSS
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
      <defs>
        <filter id="glow-${status}" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <circle cx="12" cy="12" r="10" fill="${color}" stroke="white" stroke-width="2" ${status === 'executing' ? 'filter="url(#glow-executing)"' : ''}/>
      ${status === 'executing' ? '<circle cx="12" cy="12" r="6" fill="white" opacity="0.5"/>' : ''}
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Draw order for overlapping markers. Two locations in the same city (a private
 * lab and the shared datacenter, say) geocode to the identical point — the one
 * with live activity has to win, or the dormant gray dot buries it.
 */
const STATUS_Z_INDEX: Record<LocationStatus, number> = {
  executing: 50,
  error: 40,
  warning: 30,
  success: 20,
  inactive: 10,
};

// Geocoded coordinates type
interface GeocodedLocation extends LocationExecutionStats {
  geocodedCoordinates?: { latitude: number; longitude: number };
}

export const WorldMap: React.FC<WorldMapProps> = ({
  height = '400px',
  onLocationClick,
  refreshInterval = 60000,
}) => {
  const navigate = useNavigate();
  const fmt = useDateFormat();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<GeocodedLocation | null>(null);
  const [geocodingComplete, setGeocodingComplete] = useState(false);
  // Bumped whenever the geocode cache gains an entry, so the derived location
  // list recomputes (a ref mutation can't trigger a render on its own).
  const [geocodeVersion, setGeocodeVersion] = useState(0);
  // `null` = resolved-as-failed, and is deliberately cached like a hit.
  const geocodeCacheRef = useRef<Map<string, { lat: number; lng: number } | null>>(new Map());
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || '';

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const {
    locationStats,
    loading,
    error,
    refresh,
  } = useLocationExecutionStatus({
    hoursWindow: 24,
    refreshInterval,
  });

  // Only locations WITHOUT their own coordinates need a lookup. Most carry
  // exact lat/lng on the record already; geocoding those spent a round-trip
  // rediscovering a coordinate we were handed.
  const geocodeQueries = useMemo(() => {
    const queries = new Set<string>();
    for (const location of locationStats) {
      if (location.coordinates) continue;
      const q = [location.city, location.country].filter(Boolean).join(', ');
      if (q) queries.add(q);
    }
    return Array.from(queries).sort();
  }, [locationStats]);

  // Stable key for "which places still need resolving". The 30 s refresh hands
  // back a fresh array every tick; keying the lookup on the array identity
  // restarted the whole serial walk twice a minute, forever.
  const geocodeSignature = geocodeQueries.join('|');

  // Resolve missing coordinates. Fills the cache and bumps a version so the
  // derived list below recomputes — status updates never wait on this.
  useEffect(() => {
    if (!isLoaded || !geocodeQueries.length) {
      setGeocodingComplete(true);
      return;
    }

    let cancelled = false;
    const resolveMissing = async () => {
      const geocoder = new google.maps.Geocoder();
      const pending = geocodeQueries.filter((q) => !geocodeCacheRef.current.has(q));
      if (!pending.length) {
        setGeocodingComplete(true);
        return;
      }

      setGeocodingComplete(false);
      for (const geocodeQuery of pending) {
        if (cancelled) return;
        try {
          const response = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
            geocoder.geocode({ address: geocodeQuery }, (results, status) => {
              if (status === 'OK' && results && results.length > 0) {
                resolve(results);
              } else {
                reject(new Error(`Geocoding failed: ${status}`));
              }
            });
          });
          const { lat, lng } = response[0].geometry.location;
          geocodeCacheRef.current.set(geocodeQuery, { lat: lat(), lng: lng() });
        } catch (err) {
          console.warn(`Failed to geocode "${geocodeQuery}":`, err);
          // Remember the failure. Without this, a place Google can't resolve
          // pays a fresh round-trip on every single refresh.
          geocodeCacheRef.current.set(geocodeQuery, null);
        }
        // Publish each result as it lands so markers appear progressively.
        if (!cancelled) setGeocodeVersion((v) => v + 1);
      }

      if (!cancelled) setGeocodingComplete(true);
    };

    resolveMissing();
    return () => { cancelled = true; };
  }, [isLoaded, geocodeSignature]);

  // Derived, not stored: live status flows straight through from locationStats
  // on every refresh, while coordinates come from whatever the cache has
  // resolved so far.
  const geocodedLocations = useMemo<GeocodedLocation[]>(() => {
    return locationStats.map((location) => {
      if (location.coordinates) return { ...location };
      const q = [location.city, location.country].filter(Boolean).join(', ');
      const cached = q ? geocodeCacheRef.current.get(q) : undefined;
      return cached
        ? { ...location, geocodedCoordinates: { latitude: cached.lat, longitude: cached.lng } }
        : { ...location };
    });
    // geocodeVersion is the cache-mutation signal — the ref itself can't trigger one.
  }, [locationStats, geocodeVersion]);

  // Helper to get coordinates (prefer geocoded city-level for privacy)
  const getCoords = (location: GeocodedLocation) => {
    return location.geocodedCoordinates || location.coordinates;
  };

  // Fit bounds when geocoded locations change
  useEffect(() => {
    if (!mapRef.current || geocodedLocations.length === 0 || !mapLoaded || !geocodingComplete) return;

    const locationsWithCoords = geocodedLocations.filter((l) => getCoords(l));
    if (locationsWithCoords.length === 0) return;

    if (locationsWithCoords.length === 1) {
      const coords = getCoords(locationsWithCoords[0])!;
      mapRef.current.setCenter({
        lat: coords.latitude,
        lng: coords.longitude,
      });
      mapRef.current.setZoom(6);
    } else {
      const bounds = new google.maps.LatLngBounds();
      locationsWithCoords.forEach((l) => {
        const coords = getCoords(l)!;
        bounds.extend({
          lat: coords.latitude,
          lng: coords.longitude,
        });
      });
      mapRef.current.fitBounds(bounds, 50);
    }
  }, [geocodedLocations, mapLoaded, geocodingComplete]);

  const handleMarkerClick = useCallback((location: GeocodedLocation) => {
    setSelectedLocation(location);
  }, []);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapLoaded(true);
  }, []);

  const handleViewDetails = useCallback(() => {
    if (selectedLocation && onLocationClick) {
      onLocationClick(selectedLocation.locationId);
    }
  }, [selectedLocation, onLocationClick]);

  // Calculate summary stats (use geocodedLocations for consistency)
  const summaryStats = {
    total: geocodedLocations.length,
    executing: geocodedLocations.filter((l) => l.status === 'executing').length,
    success: geocodedLocations.filter((l) => l.status === 'success').length,
    warning: geocodedLocations.filter((l) => l.status === 'warning').length,
    error: geocodedLocations.filter((l) => l.status === 'error').length,
    inactive: geocodedLocations.filter((l) => l.status === 'inactive').length,
  };

  // No API key configured
  if (!apiKey) {
    return (
      <Card className="p-6 flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-semibold text-foreground mb-2">Map Not Available</p>
          <p className="text-sm text-muted-foreground">
            Google Maps API key is not configured.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden" style={{ height }}>
      {/* Status Legend */}
      <div className="absolute top-4 left-4 z-10 bg-card/95 backdrop-blur-sm rounded-lg p-3 border border-border shadow-lg">
        <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
          Test Status
        </div>
        <div className="space-y-1.5">
          {(['executing', 'success', 'warning', 'error', 'inactive'] as LocationStatus[]).map((status) => {
            const Icon = STATUS_ICONS[status];
            const count = summaryStats[status];
            return (
              <div key={status} className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${status === 'executing' ? 'animate-pulse' : ''}`}
                  style={{ backgroundColor: STATUS_COLORS[status] }}
                />
                <span className="text-xs text-foreground flex-1">{getStatusLabel(status)}</span>
                {count > 0 && (
                  <span className="text-xs text-muted-foreground font-mono">{count}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Loading Overlay. Deliberately NOT gated on `geocodingComplete`: sites
          resolve progressively, and blanking the whole map until the last one
          lands hid every marker that was already placed. Once we have anything
          to draw, show the map and let the stragglers arrive. */}
      {(!isLoaded || ((loading || !geocodingComplete) && geocodedLocations.length === 0)) && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-20">
          <div className="flex flex-col items-center gap-2">
            <Spinner size="lg" />
            <p className="text-sm text-muted-foreground">
              {!isLoaded ? 'Loading map...' : !geocodingComplete ? 'Locating test sites...' : 'Loading...'}
            </p>
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {(loadError || error) && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-status-error mx-auto mb-2" />
            <p className="text-status-error font-medium">Error loading map</p>
            <p className="text-sm text-muted-foreground">
              {loadError?.message || error}
            </p>
          </div>
        </div>
      )}

      {/* Empty State Overlay */}
      {!loading && isLoaded && geocodingComplete && geocodedLocations.filter((l) => getCoords(l)).length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
          <div className="text-center max-w-md px-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Globe className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No Test Locations Found
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {geocodedLocations.length === 0
                ? 'No locations with online devices or recent test runs were found for your organization.'
                : "Your locations don't have city information configured. Add city and country to your locations to display them on the map."}
            </p>
            <Button
              onClick={() => navigate('/edges')}
              size="sm"
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Manage Locations
            </Button>
          </div>
        </div>
      )}

      {isLoaded && (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={2}
          options={getMapOptions(mapId || undefined, isDarkMode)}
          onLoad={handleMapLoad}
        >
          {mapLoaded &&
            geocodedLocations
              .filter((l) => getCoords(l))
              .map((location) => {
                const coords = getCoords(location)!;
                const isSelected = selectedLocation?.locationId === location.locationId;
                const size = isSelected ? 20 : 14;
                const position = { lat: coords.latitude, lng: coords.longitude };
                const iconUrl = generateMarkerSvg(location.status, isSelected);
                const zIndex = STATUS_Z_INDEX[location.status] + (isSelected ? 100 : 0);

                // AdvancedMarkerElement requires a Map ID; without one, fall back
                // to the legacy Marker so markers still render in dev/unconfigured envs.
                return mapId ? (
                  <AdvancedMarker
                    key={location.locationId}
                    position={position}
                    onClick={() => handleMarkerClick(location)}
                    iconUrl={iconUrl}
                    iconSize={{ width: size, height: size }}
                    title={location.locationName}
                    zIndex={zIndex}
                  />
                ) : (
                  <Marker
                    key={location.locationId}
                    position={position}
                    onClick={() => handleMarkerClick(location)}
                    icon={{
                      url: iconUrl,
                      scaledSize: new google.maps.Size(size, size),
                      anchor: new google.maps.Point(size / 2, size / 2),
                    }}
                    title={location.locationName}
                    zIndex={zIndex}
                  />
                );
              })}

          {selectedLocation && getCoords(selectedLocation) && (
            <InfoWindow
              position={{
                lat: getCoords(selectedLocation)!.latitude,
                lng: getCoords(selectedLocation)!.longitude,
              }}
              onCloseClick={() => setSelectedLocation(null)}
            >
              <div className="min-w-[240px] p-1">
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-3 h-3 rounded-full ${selectedLocation.status === 'executing' ? 'animate-pulse' : ''}`}
                    style={{ backgroundColor: STATUS_COLORS[selectedLocation.status] }}
                  />
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {selectedLocation.locationName}
                  </h3>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: STATUS_COLORS[selectedLocation.status] }}
                  >
                    {getStatusLabel(selectedLocation.status)}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                  <div>
                    <span className="text-gray-400">24h Tests:</span>{' '}
                    <span className="font-medium">{selectedLocation.totalExecutions24h}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Success:</span>{' '}
                    <span className="font-medium text-green-600">{selectedLocation.successRate}%</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Passed:</span>{' '}
                    <span className="font-medium text-green-600">{selectedLocation.successCount}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Failed:</span>{' '}
                    <span className="font-medium text-red-600">{selectedLocation.failedCount}</span>
                  </div>
                </div>

                {/* Modems */}
                <div className="flex items-center gap-1 text-gray-500 text-xs mb-3">
                  <Smartphone className="w-3 h-3" />
                  <span>
                    {selectedLocation.onlineModemCount || 0}/{selectedLocation.modemCount || 0} modems online
                  </span>
                </div>

                {/* Running indicator */}
                {selectedLocation.runningCount > 0 && (
                  <div className="flex items-center gap-1 text-blue-600 text-xs mb-3">
                    <Activity className="w-3 h-3 animate-pulse" />
                    <span>{selectedLocation.runningCount} test(s) running</span>
                  </div>
                )}

                {/* Last execution */}
                {selectedLocation.lastExecutionAt && (
                  <p className="text-xs text-gray-400 mb-3">
                    Last test: {fmt.formatDateTime(selectedLocation.lastExecutionAt)}
                  </p>
                )}

                {onLocationClick && (
                  <button
                    onClick={handleViewDetails}
                    className="w-full px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    View Details
                  </button>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </Card>
  );
};

export default WorldMap;
